import { inject, Injectable } from '@angular/core';
import { AuthenticationService } from '../../features/auth/services/authentication.service';

export const Position = {
  DIRECTOR: 'DIRECTOR',
  ACCOUNTANT: 'ASSISTANT_DIRECTOR',
  TEACHER: 'TEACHER',
} as const;

export const Role = {
  SAAS_ADMIN: 'SAAS_ADMIN',
  STUDENT: 'STUDENT',
  SCHOOL_STAFF: 'SCHOOL_STAFF',
  GUARDIAN: 'GUARDIAN',
} as const;

export const Permission = {
  MANAGE_SCHOOL_CONFIG: 'MANAGE_SCHOOL_CONFIG',
  MANAGE_ACADEMIC_STRUCTURE: 'MANAGE_ACADEMIC_STRUCTURE',
  MANAGE_STAFF: 'MANAGE_STAFF',
  MANAGE_FINANCES: 'MANAGE_FINANCES',
  VIEW_FINANCES: 'VIEW_FINANCES',
  MANAGE_ENROLLMENT: 'MANAGE_ENROLLMENT',
  MANAGE_STUDENTS: 'MANAGE_STUDENTS',
  VIEW_STUDENTS: 'VIEW_STUDENTS',
  RECORD_GRADES: 'RECORD_GRADES',
} as const;

export type PermissionType = (typeof Permission)[keyof typeof Permission];
export type RoleType = (typeof Role)[keyof typeof Role];
export type PositionType = (typeof Position)[keyof typeof Position];

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private authService = inject(AuthenticationService);
  private user = this.authService.getUser();

  /**
   * check if the current user has the specified role
   * @param role the role to check against the current user's roles
   * @returns true if the current user has the specified role, false otherwise
   */
  hasRole(role: RoleType): boolean {
    return this.user()?.roles.some((r) => r === role) ?? false;
  }

  hasAnyRole(roles: readonly RoleType[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }

  /**
   * check if the current user has the specified permission
   * @param permission the permission to check against the current user's permissions
   * @returns true if the current user has the specified permission, false otherwise
   */

  hasPermission(permission: PermissionType): boolean {
    const schoolId = this.authService.activeSchoolId();
    return schoolId ? (this.user()?.permissions[schoolId]?.permissions.includes(permission) ?? false) : false;
  }

  /**
   * check if the current user has any of the specified permissions
   * @param permissions the permissions to check against the current user's permissions
   * @returns true if the current user has any of the specified permissions, false otherwise
   */
  hasAnyPermission(permissions: readonly PermissionType[]): boolean {
    return permissions.some((permission) => this.hasPermission(permission));
  }

  hasDirectPermission(permission: PermissionType): boolean {
    return this.hasPermission(permission);
  }

  isSaasAdmin(): boolean {
    return this.hasRole(Role.SAAS_ADMIN);
  }
}
